import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import { Box, Center, Flex, Spinner, Table, Tbody, Text, Th, Thead, Tr } from '@chakra-ui/react'
import { toPlural } from '@chem-po/core'
import { EncounterType, encounterTypeName } from '@hb/shared'
import { useExamListGroup } from '../context'
import { EncounterSelection } from '../types'
import { EncounterListHeader } from './EncounterListHeader'
import { EncounterRowProps, encounterRowRecord } from './EncounterRow'
import { EncounterListProvider, useExamList } from './context'
import { useProfileEncounterListProps } from './hooks'

const EncountersListBody = () => {
  const {
    encounterEntries,
    dateAsc,
    toggleDateAsc,
    type,
    patientEncountersData: encounters,
    onOpenExam,
  } = useExamList()

  const { isSelecting, selectedIds, onToggleExam } = useExamListGroup()

  const { loading, error } = encounters

  const { columns, Row } = encounterRowRecord[type]
  const ExamRow = Row as React.FC<EncounterRowProps>
  if (encounterEntries.length > 0) {
    return (
      <Box overflowX="auto" minH="150px">
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th cursor="pointer" userSelect="none" py={1.5} onClick={toggleDateAsc}>
                <Flex align="center" gap={1}>
                  Date
                  {dateAsc ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </Flex>
              </Th>
              {columns.slice(1).map(col => (
                <Th py={1.5} key={col}>
                  {col}
                </Th>
              ))}
              {isSelecting ? (
                <Th py={1.5} w="38px" px={0} borderLeft="1px solid" borderColor="gray.100" />
              ) : null}
            </Tr>
          </Thead>
          <Tbody>
            {encounterEntries.map(exam => (
              <ExamRow
                key={exam.id}
                encounter={exam}
                encounterId={exam.id}
                onOpenEncounter={onOpenExam}
                isSelecting={isSelecting}
                isChecked={selectedIds[type]?.includes(exam.id)}
                onToggle={() => onToggleExam(type, exam.id)}
              />
            ))}
          </Tbody>
        </Table>
      </Box>
    )
  } else if (loading) {
    return (
      <Center minH="150px" p={4}>
        <Spinner size="lg" color="gray.400" />
      </Center>
    )
  } else if (error) {
    return (
      <Center minH="150px" p={4}>
        <Text fontSize="lg" color="red.500">
          {error}
        </Text>
      </Center>
    )
  } else {
    return (
      <Center minH="150px" p={4}>
        <Text fontSize="lg" color="gray.500">
          No {toPlural(encounterTypeName[type])} found
        </Text>
      </Center>
    )
  }
}

export const ProfileEncounterList = ({
  type,
  onOpenExam,
}: {
  type: EncounterType
  onOpenExam: (exam: EncounterSelection) => void
}) => {
  const props = useProfileEncounterListProps(type)
  return (
    <EncounterListProvider onOpenExam={onOpenExam} {...props}>
      <Box
        w="100%"
        bg="white"
        borderBottom="1px solid"
        borderColor="gray.200"
        _dark={{ bg: 'gray.900', borderColor: 'gray.700' }}>
        <EncounterListHeader />
        <EncountersListBody />
      </Box>
    </EncounterListProvider>
  )
}
